import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from 'components/uikit/modal/Modal.tsx';
import { Button, ButtonColor } from 'components/uikit/buttons/Button.tsx';
import { useScopedTranslation } from 'application/localization/useScopedTranslation.ts';
import { useAdvancedForm } from 'helpers/form/useAdvancedForm.ts';
import styles from './ModalStyles.module.scss';
import {
  FilesClient,
  ReasonForReturn,
  RepairDeviceDto,
  RepairsOrderClient,
  RepairsOrderQuery,
} from 'services/api/api-client.ts';
import { useWatch } from 'react-hook-form';
import clsx from 'clsx';
import { AxiosError } from 'axios';
import { DeviceInfoScreen } from './DeviceInfoScreen.tsx';
import { ReceiptScreen } from './ReceiptScreen.tsx';
import { AdditionalInfoScreen } from './AdditionalInfoScreen.tsx';
import { UseTranslationResponse } from 'react-i18next';
import { reasonConfigs } from '../repairReasonOptions.ts';
import { CustomModal } from '../../../../components/uikit/modal/CustomModal.tsx';
import { errorToString } from '../../../../helpers/error-helpers.ts';

export type ExternalDeviceInfo = (Form & { id: number }) | null;

type AddDeviceModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (draftId?: number) => void;
  defaultFormData: ExternalDeviceInfo;
};

export type Option = {
  key: string;
  text: string;
};

export type FoundDeviceOption = Option & {
  serialNumber: string;
  article: string;
  deviceName: string;
};

export type Form = {
  serialNumber: string;
  selectedReasons: Option[];
  selectedErrors: Option[];
  selectedFoundDevice: FoundDeviceOption | null;
  deviceDescription: string;
  errorDescription: string;
  receiptFile: File | null;
  receiptFileName: string | null;
  receiptFileId: string | null;
  replacementReceived: boolean | null;
  repairLimitEuro: string;
  workRequiredImmediately: boolean;
  ticketId: string;
};

export const notFoundStatusCode = 404;

const defineButtonName = (
  modalStep: number,
  modalStepsNumber: number,
  isFileSelected: boolean,
  i18n: UseTranslationResponse<'translation', 'Page.CreateRepairRequest'>,
  isEditing: boolean,
) => {
  if (modalStep === 0) {
    return i18n.t('next_step');
  }

  if (modalStep === modalStepsNumber - 1) {
    if (isEditing) {
      return i18n.t('modal_save_changes');
    }

    return i18n.t('add_device');
  }

  if (!isFileSelected) {
    return i18n.t('skip');
  }

  return i18n.t('next_step');
};

export const AddDeviceModal = ({
  isOpen,
  onClose,
  onSubmit,
  defaultFormData,
}: AddDeviceModalProps) => {
  const i18n = useScopedTranslation('Page.CreateRepairRequest');

  const [searchedSerialNumber, setSearchedSerialNumber] = useState('');
  const [modalStep, setModalStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isCloseAddingModalShown, setIsCloseAddingModalShown] = useState(false);
  const [submitErrorText, setSubmitErrorText] = useState('');

  const wasResetRef = useRef<boolean>(false);

  const setSearchedSN = (newSN: string) => {
    setSearchedSerialNumber(newSN);
  };

  const form = useAdvancedForm<Form>(
    async () => {
      if (!isDeviceLoaded) {
        form.setError('serialNumber', {
          message: i18n.t('required'),
        });
        return;
      }

      if (hasMultipleDevices && !selectedFoundDevice) {
        form.setError('selectedFoundDevice', {
          message: i18n.t('required'),
        });
        return;
      }

      if (deviceNotFound) {
        if (!deviceDescription) {
          form.setError('deviceDescription', {
            message: i18n.t('required'),
          });
          return;
        }

        if (!errorDescription) {
          form.setError('errorDescription', {
            message: i18n.t('required'),
          });
          return;
        }
      } else {
        if (!selectedReasons.length) {
          form.setError('selectedReasons', {
            message: i18n.t('required'),
          });
          return;
        }

        if (!selectedErrors.length) {
          form.setError('selectedErrors', {
            message: i18n.t('required'),
          });
          return;
        }
      }

      if (modalStep + 1 === modalSteps.length) {
        try {
          setSubmitErrorText('');
          setIsLoading(true);

          const reasonsForReturn = reasonConfigs
            .map((reason) => reason.value)
            .filter((reason) =>
              selectedReasons.some((selected) => selected.key === reason),
            );

          let salesReceiptFileId: string | null = receiptFileId;
          if (receiptFile) {
            const uploadedFile = await FilesClient.uploadFile(
              undefined,
              undefined,
              {
                data: receiptFile,
                fileName: receiptFile.name,
              },
            );
            salesReceiptFileId = uploadedFile.id;
          }

          if (defaultFormData?.id) {
            await RepairsOrderClient.patchRepairOrderDraft(defaultFormData.id, {
              serialNumber: deviceSerialNumber,
              serviceProduct:
                selectedFoundDevice?.key ??
                device.data?.serviceProductId ??
                null,
              article: deviceArticle,
              deviceName: deviceName,
              withoutSerialNumber: deviceNotFound,
              deviceDescription: deviceDescription,
              reasonsForReturn: reasonsForReturn,
              additionalErrorDescription: errorDescription,
              selectedErrors: selectedErrors,
              salesReceiptFileId,
              replacementAlreadyReceived: replacementReceived,
              repairLimitEuro: Number(repairLimitEuro),
              workRequiredImmediately: workRequiredImmediately,
              ticketId,
            });
          } else {
            await RepairsOrderClient.createRepairOrderDraft({
              serialNumber: deviceSerialNumber,
              serviceProduct:
                selectedFoundDevice?.key ??
                device.data?.serviceProductId ??
                null,
              article: deviceArticle,
              deviceName: deviceName,
              withoutSerialNumber: deviceNotFound,
              deviceDescription: deviceDescription,
              reasonsForReturn: reasonsForReturn,
              additionalErrorDescription: errorDescription,
              selectedErrors: selectedErrors,
              salesReceiptFileId: salesReceiptFileId ?? null,
              replacementAlreadyReceived: replacementReceived ?? null,
              repairLimitEuro: Number(repairLimitEuro),
              workRequiredImmediately: workRequiredImmediately,
              ticketId,
            });
          }

          handleClose();
          onSubmit(defaultFormData?.id);
        } catch (e) {
          setSubmitErrorText(errorToString(e));
        } finally {
          setIsLoading(false);
        }
      } else {
        nextModalStep();
      }
    },
    {
      defaultValues: {
        serialNumber: defaultFormData?.serialNumber ?? '',
        selectedReasons: defaultFormData?.selectedReasons ?? [],
        selectedErrors: defaultFormData?.selectedErrors ?? [],
        selectedFoundDevice: defaultFormData
          ? {
              serialNumber: defaultFormData.serialNumber ?? '',
              deviceName: defaultFormData.selectedFoundDevice?.deviceName ?? '',
              article: defaultFormData.selectedFoundDevice?.article ?? '',
              key: defaultFormData.selectedFoundDevice?.key ?? '',
              text: '',
            }
          : null,
        deviceDescription: defaultFormData?.deviceDescription ?? '',
        errorDescription: defaultFormData?.errorDescription ?? '',
        receiptFile: null,
        receiptFileName: defaultFormData?.receiptFileName ?? null,
        receiptFileId: defaultFormData?.receiptFileId ?? null,
        ticketId: defaultFormData?.ticketId ?? '',
        workRequiredImmediately:
          defaultFormData?.workRequiredImmediately ?? false,
        repairLimitEuro: defaultFormData?.repairLimitEuro ?? '',
        replacementReceived: defaultFormData?.replacementReceived ?? null,
      },
    },
  );

  const [
    selectedReasons,
    selectedFoundDevice,
    deviceDescription,
    errorDescription,
    receiptFile,
    receiptFileId,
    workRequiredImmediately,
    repairLimitEuro,
    replacementReceived,
    serialNumber,
    selectedErrors,
    ticketId,
  ] = useWatch({
    control: form.control,
    name: [
      'selectedReasons',
      'selectedFoundDevice',
      'deviceDescription',
      'errorDescription',
      'receiptFile',
      'receiptFileId',
      'workRequiredImmediately',
      'repairLimitEuro',
      'replacementReceived',
      'serialNumber',
      'selectedErrors',
      'ticketId',
    ],
  });

  const device = RepairsOrderQuery.useRequestDeviceQuery<
    RepairDeviceDto,
    AxiosError
  >(
    {
      serialNumber: searchedSerialNumber || undefined,
      serviceProductId: selectedFoundDevice?.key || undefined,
      userLanguage: i18n.i18n.language,
    },
    {
      staleTime: 60 * 1000,
      enabled: !!searchedSerialNumber,
      throwOnError: false,
      retry: false,
    },
  );
  const foundDeviceOptions = useMemo<FoundDeviceOption[]>(
    () =>
      (device.data?.foundDevices ?? []).map((item) => ({
        key: item.serviceProductId,
        text: `${item.serialNumber} - ${item.deviceName}`,
        serialNumber: item.serialNumber,
        article: item.article,
        deviceName: item.deviceName,
      })),
    [device.data?.foundDevices],
  );

  const deviceName =
    selectedFoundDevice?.deviceName || device.data?.deviceName || '';
  const deviceArticle =
    selectedFoundDevice?.article || device.data?.article || '';
  const deviceSerialNumber =
    selectedFoundDevice?.serialNumber ||
    device.data?.serialNumber ||
    serialNumber;

  const deviceNotFound = device?.error?.response?.status === notFoundStatusCode;
  const hasMultipleDevices = !!foundDeviceOptions.length;
  const isDeviceLoaded = device.isFetched;

  const isReceiptStepNeeded = selectedReasons.find(
    (reason) =>
      reason.key === ReasonForReturn.Warranty ||
      reason.key === ReasonForReturn.ReturnForCredit,
  );

  useEffect(() => {
    if (defaultFormData?.serialNumber) {
      setSearchedSN(defaultFormData?.serialNumber);
    }
  }, [defaultFormData?.serialNumber]);

  const handleClose = () => {
    onClose();
    setIsCloseAddingModalShown(false);
    setSearchedSN('');
    wasResetRef.current = false;
  };

  useEffect(() => {
    if (isOpen) {
      form.reset();
      setModalStep(0);
      setSubmitErrorText('');
    }
  }, [isOpen]);

  const onModalClose = () => {
    if (Object.keys(form.formState.dirtyFields).length > 0) {
      setIsCloseAddingModalShown(true);
    } else {
      handleClose();
    }
  };

  const isFirefox = 'InstallTrigger' in window;

  const modalSteps = [
    {
      render: () => (
        <DeviceInfoScreen
          form={form}
          isDeviceLoaded={isDeviceLoaded}
          deviceNotFound={deviceNotFound}
          hasMultipleDevices={hasMultipleDevices}
          device={device}
          setSearchedSN={setSearchedSN}
          wasResetRef={wasResetRef}
        />
      ),
    },
    ...(isReceiptStepNeeded
      ? [
          {
            render: () => <ReceiptScreen form={form} />,
          },
        ]
      : []),
    {
      render: () => <AdditionalInfoScreen form={form} />,
    },
  ];

  const nextModalStep = () => {
    setModalStep((prev) => {
      const nextStepNumber = prev + 1;

      if (nextStepNumber > modalSteps.length) {
        return prev;
      }

      return nextStepNumber;
    });

    if (!isReceiptStepNeeded) {
      form.setValue('receiptFile', null);
    }
  };

  const previousModalStep = () => {
    setModalStep((prev) => {
      const previousStepNumber = prev - 1;

      if (previousStepNumber < 0) {
        return prev;
      }

      return previousStepNumber;
    });
    setSubmitErrorText('');
  };

  const CurrentStep = modalSteps[modalStep];

  if (!CurrentStep) {
    return null;
  }

  return (
    <Modal
      containerClassName={styles.addDeviceModalContainer}
      doNotCloseOnOutsideClick={true}
      contentClassName={clsx(
        styles.overflowYScroll,
        isFirefox && styles.paddingRight24,
      )}
      isOpen={isOpen}
      onClose={onModalClose}
      title={
        <div className={styles.addDeviceModalHeader}>
          <div className={styles.addDeviceModalTitle}>
            {modalStep === 0
              ? i18n.t('modal_device_info')
              : modalStep === modalSteps.length - 1
              ? i18n.t('modal_additional_info')
              : i18n.t('modal_sales_receipt')}
          </div>
          <div className={styles.addDeviceModalStep}>
            {modalStep + 1}/{modalSteps.length}
          </div>
        </div>
      }
    >
      {CurrentStep.render()}
      <div
        className={clsx(
          styles.addDeviceModalFooter,
          submitErrorText ? styles.spaceBetween : styles.alignSelfEnd,
        )}
      >
        {submitErrorText && (
          <div className={clsx(styles.unexpectedServerError)}>
            {submitErrorText}
          </div>
        )}
        <div className={styles.buttonContainer}>
          {modalStep !== 0 && (
            <Button
              title={i18n.t('back')}
              color={ButtonColor.Secondary}
              disabled={modalStep === 0}
              onClick={previousModalStep}
              testId={'add-device-modal-previous-step'}
            />
          )}
          <Button
            title={defineButtonName(
              modalStep,
              modalSteps.length,
              !!receiptFile || !!receiptFileId,
              i18n,
              !!defaultFormData,
            )}
            color={ButtonColor.Primary}
            onClick={form.handleSubmitDefault}
            testId={'add-device-modal-next-step'}
            loading={isLoading}
            disabled={isLoading}
          />
        </div>
      </div>
      <CustomModal
        isOpen={isCloseAddingModalShown}
        bodyText={i18n.t('leave_modal_body')}
        onSubmit={handleClose}
        onCancel={() => setIsCloseAddingModalShown(false)}
        cancelButtonText={i18n.t('cancel')}
        submitButtonText={i18n.t('discard_changes')}
        titleText={
          !!defaultFormData
            ? i18n.t('modal_leave_editing_device')
            : i18n.t('leave_modal_title')
        }
        containerClassName={styles.modalBodyWidth480}
        submitButtonType={ButtonColor.Warning}
        cancelButtonType={ButtonColor.Secondary}
      />
    </Modal>
  );
};
